import { Flex, DatePicker, Card, Select, Button } from "antd"
import { useTranslation } from "react-i18next"
import { Title } from "ui/Typography"
import { useCallback, useEffect, useMemo, useState } from "react"
import EmailHistoryTable from "./EmailHistoryTable"
import SearchInput from "ui/SearchInput"
import WithEmptyState from "ui/WithEmptyStateWrapper"
import { useDispatch, useSelector } from "react-redux"
import { getEmailLogs } from "store/clients"
import PropTypes from "prop-types"
import { debounce } from "lodash"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import moment from "moment"
import styles from "../index.module.css"
import TemplateView from "../TemplateView"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import { Announcement } from "ui/Icon/DSIcon/Announcement"

const { RangePicker } = DatePicker
const dateFormat = "MM/DD/YYYY"

const EmailsTab = ({ active, id }) => {
  const { t } = useTranslation()
  const [searchBy, setSearchBy] = useState("")
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")
  const [pageSize, setPageSize] = useState(10)
  const [sortKey, setSortKey] = useState("")
  const [sortOrder, setSortOrder] = useState("")
  const [isAppend, setIsAppend] = useState(false)
  const [dateRange, setDateRange] = useState(null)
  const [selectedEmailLog, setSelectedEmailLog] = useState(null)
  const [isFiltered, setIsFiltered] = useState(false)
  const dispatch = useDispatch()
  const { data, metadata } = useSelector((store) => store.clients.emailLogs)
  const client = useSelector((state) => state.clients.client)
  const navigate = useNavigate()

  useLoaderWatchers({ watchers: ["clients.emailLogs"] })

  useEffect(() => {
    const fetchEmailLogs = async () => {
      if (active && id) {
        // Build the query parameters object
        const queryParams = new URLSearchParams()

        // Add base parameters
        queryParams.append("page", page)
        queryParams.append("per_page", pageSize)

        // Add search parameter if exists
        if (searchQuery) {
          queryParams.append("search_email", searchQuery)
        }

        // Add sort parameters if they exist
        if (sortKey) {
          queryParams.append("sort_by", sortKey)
          queryParams.append("sort_by_order", sortOrder)
        }

        if (dateRange?.startDate && dateRange?.endDate) {
          const { startDate, endDate } = dateRange
          queryParams.append("created_from", moment(startDate).format("YYYY-MM-DD"))
          queryParams.append("created_to", moment(endDate).format("YYYY-MM-DD"))
        }
        // Convert URLSearchParams to object for the API call
        await dispatch(getEmailLogs(id, isAppend, Object.fromEntries(queryParams)))
        if (searchQuery || (dateRange?.startDate && dateRange?.endDate)) {
          setIsFiltered(true)
        }
      }
    }
    fetchEmailLogs()
  }, [active, id, page, pageSize, sortKey, sortOrder, searchQuery, dateRange, dispatch, isAppend])

  // Create a stable reference to the debounced function
  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((value) => {
        setSearchQuery(value)
        setPage(1) // Reset to first page when search changes
      }, 300),
    []
  )

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearchQuery.cancel()
    }
  }, [debouncedSetSearchQuery])

  const onSortChange = (pagination, filters, sorter) => {
    setSortKey(sorter?.field)
    setSortOrder(sorter?.order === "ascend" ? "asc" : "desc")
    setPage(1) // Reset to first page when sorting changes
  }

  // Handle search input changes
  const handleSearchChange = useCallback(
    (value) => {
      setSearchBy(value)
      debouncedSetSearchQuery(value)
    },
    [debouncedSetSearchQuery]
  )

  const handlePageSizeChange = (value) => {
    setPageSize(value)
    setPage(1) // Reset to first page when page size changes
  }

  const handleDateChange = (_, dateStrings) => {
    if (dateStrings && dateStrings.length === 2 && dateStrings[0] && dateStrings[1]) {
      setDateRange({
        startDate: dateStrings[0],
        endDate: dateStrings[1]
      })
    } else {
      setDateRange(null)
    }
    setPage(1)
  }

  const onRowClick = (selectedEmailLogId) => {
    const emailLog = data.find((email) => email.id === selectedEmailLogId)
    setSelectedEmailLog(emailLog)
  }

  const handleNewEmailClick = () => {
    navigate(routes.guideClientEmailActionPath(), {
      state: { clients: [client], userIds: [client?.user?.id], templates: client?.email_templates_collection, fromTab: "email" }
    })
  }

  if (selectedEmailLog) {
    return (
      <TemplateView
        createdAt={selectedEmailLog?.created_at}
        selectedTemplateLog={selectedEmailLog}
        setSelectedTemplateLog={setSelectedEmailLog}
      />
    )
  }

  const handleSortChange = (value) => {
    const [key, order] = value.split("#")
    setSortKey(key)
    setSortOrder(order)
    setPage(1)
  }

  return (
    <FirstLoading name="clients.emailLogs">
      <Card>
        <Flex vertical gap={16}>
          <Flex justify="space-between" align="center">
            <Title level={5}>{t("client.email_history")}</Title>
            <Button type="primary" className={`${styles.ShowOnTab} px-xl py-l ml-auto`} onClick={handleNewEmailClick}>
              {t("client.new_email")}
            </Button>
          </Flex>
          <Flex gap={16} className="w-full" wrap="wrap">
            <SearchInput
              className={styles.clientSearchInput}
              searchBy={searchBy}
              setSearchBy={handleSearchChange}
              ariaLabel={t("clients.aria.search", { name: "email-history" })}
              ariaControls={t("clients.aria.search_result", { name: "email-history" })}
              placeholder={t("global.search")}
            />
            <RangePicker
              className={styles.clientRangePicker}
              format={dateFormat}
              onChange={handleDateChange}
              placeholder={["From", "To"]}
            />
            <Select className={styles.clientSortByFilter} placeholder="Sort By" onChange={handleSortChange}>
              <Select.Option value="created_at#asc">Date sent (A to Z)</Select.Option>
              <Select.Option value="created_at#desc">Date sent (Z to A)</Select.Option>
              <Select.Option value="subject#asc">Subject (A to Z)</Select.Option>
              <Select.Option value="subject#desc">Subject (Z to A)</Select.Option>
            </Select>
            <Button type="primary" className={`${styles.hideOnTab} px-xl py-l ml-auto`} onClick={handleNewEmailClick}>
              {t("client.new_email")}
            </Button>
          </Flex>
          <WithEmptyState
            name="Email"
            hasItems={data?.length > 0}
            fallbackTitle={isFiltered ? t("client.no_results_found") : t("client.empty_email_history")}
            fallbackImageSrc={<Announcement />}
          >
            <EmailHistoryTable
              data={data}
              page={page}
              pageSize={pageSize}
              sortKey={sortKey}
              sortOrder={sortOrder}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              onSortChange={onSortChange}
              metadata={metadata}
              onRowClick={onRowClick}
              setIsAppend={setIsAppend}
            />
          </WithEmptyState>
        </Flex>
      </Card>
    </FirstLoading>
  )
}

EmailsTab.propTypes = {
  active: PropTypes.bool,
  id: PropTypes.string
}

export default EmailsTab
